import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { ValidateMembershipCoupon, UpgradePlan } from '../apis/api/MembershipApi';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import './MembershipUpgradeModal.css';

const MembershipUpgradeModal = ({ isOpen, onClose, currentPlan, newPlan, planType }) => {
  const dispatch = useDispatch();
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const company_id = Cookies.get("company_id");
  const userID = Cookies.get("userID");

  if (!isOpen) return null;

  const isUpgrade = planType === 'upgrade';
  const originalAmount = parseFloat(newPlan.fees) || 0;
  const currencySymbol = appliedCoupon?.currency_symbol || '$';

  let discountAmount = 0;
  if (appliedCoupon && originalAmount > 0) {
    // Only apply discount if there is an actual fee
    discountAmount = appliedCoupon.discount_type === 'percentage'
      ? Math.min((originalAmount * parseFloat(appliedCoupon.discount)) / 100, originalAmount)
      : Math.min(parseFloat(appliedCoupon.discount) || 0, originalAmount);
  }
  const finalAmount = Math.max(0, originalAmount - discountAmount);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) { toast.error('Please enter a coupon code'); return; }
    setIsValidating(true);
    try {
      const membershipId = newPlan.membership_id.split(':')[0];
      const response = await dispatch(ValidateMembershipCoupon({
        coupon_code: couponCode.trim(),
        membership_id: membershipId,
        company_id
      })).unwrap();
      if (response.status === 'success') {
        setAppliedCoupon(response.data);
        toast.success('Coupon applied!');
      } else {
        toast.error(response.message || 'Invalid coupon code');
      }
    } catch (error) {
      toast.error(error.message || 'Invalid or expired coupon');
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const membershipId = newPlan.membership_id.split(':')[0];
      const requestData = { company_id, membership_id: membershipId, userID };
      if (appliedCoupon) {
        requestData.couponData = {
          coupon_id: appliedCoupon.coupon_id,
          coupon_code: appliedCoupon.coupon_code,
          discount_amount: discountAmount,
          original_amount: originalAmount,
          final_amount: finalAmount
        };
      }
      const response = await dispatch(UpgradePlan(requestData)).unwrap();
      if (response.status === 'success') {
        toast.success(response.msz || 'Request submitted successfully!');
        onClose();
        setTimeout(() => window.location.reload(), 1500);
      } else {
        toast.error(response.msz || 'Something went wrong');
      }
    } catch {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mum-overlay" onClick={onClose}>
      <div className="mum-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="mum-header">
          <div className="mum-header-icon">
            <i className={`bx ${isUpgrade ? 'bx-trending-up' : 'bx-trending-down'}`}></i>
          </div>
          <div className="mum-header-text">
            <h2 className="mum-title">{isUpgrade ? 'Upgrade' : 'Downgrade'} Membership</h2>
            <p className="mum-subtitle">Review your plan change before submitting</p>
          </div>
          <button className="mum-close" onClick={onClose}>
            <i className='bx bx-x'></i>
          </button>
        </div>

        <div className="mum-body">

          {/* Plan Comparison */}
          <div className="mum-plans-wrap">
            {/* Current Plan */}
            <div className="mum-plan mum-plan--current">
              <div className="mum-plan-tag">Current</div>
              <div className="mum-plan-name">{currentPlan.membership_name}</div>
              <div className="mum-plan-price">
                <span className="mum-plan-currency">$</span>
                <span className="mum-plan-amount">{currentPlan.fees}</span>
              </div>
              <div className="mum-plan-duration">
                <i className='bx bx-time-five'></i> {currentPlan.duration_in_months} months
              </div>
            </div>

            {/* Arrow */}
            <div className="mum-transition">
              <div className={`mum-transition-icon ${isUpgrade ? 'mum-transition--up' : 'mum-transition--down'}`}>
                <i className={`bx ${isUpgrade ? 'bxs-up-arrow' : 'bxs-down-arrow'}`}></i>
              </div>
              <span className="mum-transition-label">{isUpgrade ? 'Upgrade' : 'Downgrade'}</span>
            </div>

            {/* New Plan */}
            <div className={`mum-plan mum-plan--new ${isUpgrade ? 'mum-plan--upgrade' : 'mum-plan--downgrade'}`}>
              <div className="mum-plan-tag">{isUpgrade ? 'New Plan' : 'New Plan'}</div>
              <div className="mum-plan-name">{newPlan.membership_name}</div>
              <div className="mum-plan-price">
                <span className="mum-plan-currency">$</span>
                <span className="mum-plan-amount">{newPlan.fees}</span>
              </div>
              <div className="mum-plan-duration">
                <i className='bx bx-time-five'></i> {newPlan.duration_in_months} months
              </div>
            </div>
          </div>

          {/* Coupon */}
          <div className="mum-section">
            <div className="mum-section-label">
              <i className='bx bx-purchase-tag'></i> Discount Coupon
              <span className="mum-optional">optional</span>
            </div>

            {!appliedCoupon ? (
              <div className="mum-coupon-row">
                <input
                  type="text"
                  className="mum-coupon-input"
                  placeholder="Enter coupon code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                  disabled={isValidating}
                />
                <button
                  className="mum-coupon-btn"
                  onClick={handleApplyCoupon}
                  disabled={isValidating || !couponCode.trim()}
                >
                  {isValidating
                    ? <><i className='bx bx-loader-alt bx-spin'></i> Checking...</>
                    : <><i className='bx bx-check'></i> Apply</>}
                </button>
              </div>
            ) : (
              <div className="mum-coupon-applied">
                <div className="mum-coupon-applied-left">
                  <div className="mum-coupon-check"><i className='bx bx-check'></i></div>
                  <div>
                    <div className="mum-coupon-code">{appliedCoupon.coupon_code}</div>
                    <div className="mum-coupon-saving">
                      {appliedCoupon.discount_type === 'percentage'
                        ? `${appliedCoupon.discount}% off`
                        : `${currencySymbol}${discountAmount.toFixed(2)} off`}
                    </div>
                  </div>
                </div>
                <button className="mum-coupon-remove" onClick={handleRemoveCoupon} title="Remove coupon">
                  <i className='bx bx-x'></i>
                </button>
              </div>
            )}
          </div>

          {/* Price Summary */}
          <div className="mum-summary">
            <div className="mum-summary-title">
              <i className='bx bx-receipt'></i> Price Summary
            </div>
            <div className="mum-summary-rows">
              <div className="mum-summary-row">
                <span>Plan Fee</span>
                <span>{currencySymbol}{originalAmount.toFixed(2)}</span>
              </div>
              {appliedCoupon && (
                <div className="mum-summary-row mum-summary-row--discount">
                  <span>
                    <i className='bx bx-purchase-tag'></i>
                    Discount ({appliedCoupon.coupon_code})
                  </span>
                  <span>-{currencySymbol}{discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="mum-summary-divider" />
              <div className="mum-summary-row mum-summary-row--total">
                <span>Total Payable</span>
                <span>{currencySymbol}{finalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="mum-footer">
          <button className="mum-btn mum-btn--cancel" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            className="mum-btn mum-btn--submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? <><i className='bx bx-loader-alt bx-spin'></i> Submitting...</>
              : <><i className={`bx ${isUpgrade ? 'bx-trending-up' : 'bx-trending-down'}`}></i> Confirm {isUpgrade ? 'Upgrade' : 'Downgrade'}</>}
          </button>
        </div>

      </div>
    </div>
  );
};

export default MembershipUpgradeModal;
